// externals
import { RSAA } from 'redux-api-middleware';
import type { FSA } from '@makemydeal/dr-browser-utils';

// libraries
import { MANAGER_VIEW_APP_PREFIX, apiConfigHelper, locHrefUtil } from '@makemydeal/dr-shared-ui-utils';
import { scopedActionsCreators, scopedStateSelectors } from '@makemydeal/shared-scoped-store';

// consts/enums
import {
    ACCESSORIES_OPEN_FORM,
    ADD_CATALOG_ACCESSORIES,
    ADDED_MANUAL_ACCESSORIES,
    ADD_ACCESSORIES_SELECTIONS,
    INIT_ACCESSORIES,
    REMOVE_CATALOG_ACCESSORIES,
    FOCUS_ACCESSORIES_TABLE,
    CHECK_ACCESSORIES_CATALOG_EXISTS_REQUEST,
    CHECK_ACCESSORIES_CATALOG_EXISTS_SUCCESS,
    CHECK_ACCESSORIES_CATALOG_EXISTS_FAILURE,
    ACCESSORIES_ACTIVITY_IN_PROGRESS,
    ACCESSORIES_ACTIVITY_COMPLETED,
    OPEN_ACCESSORIES_CATALOG_SLIDEOUT,
    CLOSE_ACCESSORIES_CATALOG_SLIDEOUT
} from '../actionTypes/accessoriesActionTypes';
import { DASH_START_ACCESSORIES_CATALOG } from '../actionTypes/navigationActionTypes';

// selectors
import * as vehicleSelectors from '../selectors/vehicleSelectors';
import { dealerSelectors } from '@makemydeal/dr-shared-store';

// utils
import { AccessoriesAdapter } from '../utils/manualAccessoriesAdapter';
import * as accessoriesSelectors from '../selectors/accessoriesSelectors';
import type { CatalogAccessory } from '@makemydeal/dr-dash-types';
import type { DealAccessory } from '@makemydeal/dr-shared-types';

export type OptionalOpenFormPayload = {
    scrollIntoView: boolean;
};

export type OpenFormPayload = OptionalOpenFormPayload | undefined;

export type AccessoriesOpenFormAction = FSA<typeof ACCESSORIES_OPEN_FORM, OpenFormPayload>;

export type OpenFormOptions = {
    scrollIntoView: boolean;
};

type PaymentSkipMeta = { skipPayment?: boolean };

export const startAccessoriesProgress = () => {
    return {
        type: ACCESSORIES_ACTIVITY_IN_PROGRESS
    };
};

export const completeAccessoriesProgress = () => {
    return {
        type: ACCESSORIES_ACTIVITY_COMPLETED
    };
};

export const useAccessoriesInitSuccess = (accessories: DealAccessory[]) => ({
    type: INIT_ACCESSORIES,
    payload: {
        accessories
    }
});

export const openForm = (openOptions?: OpenFormOptions): AccessoriesOpenFormAction => {
    if (openOptions?.scrollIntoView) {
        return {
            type: ACCESSORIES_OPEN_FORM,
            payload: {
                scrollIntoView: true
            }
        };
    }
    return {
        type: ACCESSORIES_OPEN_FORM
    };
};

export const focusAccessoriesTable = () => {
    return {
        type: FOCUS_ACCESSORIES_TABLE
    };
};

export const addManualAccessories = (manualAccessories: any[], meta?: PaymentSkipMeta) => ({
    type: ADDED_MANUAL_ACCESSORIES,
    payload: {
        manualAccessories: {
            accessories: manualAccessories
        }
    },
    meta
});

export const addCatalogAccessories = (catalogAccessories: CatalogAccessory[], meta?: PaymentSkipMeta) => ({
    type: ADD_CATALOG_ACCESSORIES,
    payload: {
        accessories: catalogAccessories
    },
    meta
});

export const removeCatalogAccessories = (catalogAccessories: CatalogAccessory[], meta?: PaymentSkipMeta) => ({
    type: REMOVE_CATALOG_ACCESSORIES,
    payload: {
        accessories: catalogAccessories
    },
    meta
});

export const addAccessoriesSelections =
    (skipPayment?: boolean): any =>
    (dispatch: any, getState: any) => {
        const state = getState();

        const accessoriesPayload = AccessoriesAdapter.toTargetFormat(accessoriesSelectors.getAccessories(state));
        const accessoriesSelections = accessoriesPayload.accessoryObjList;

        dispatch({
            type: ADD_ACCESSORIES_SELECTIONS,
            payload: {
                accessories: accessoriesSelections
            },
            ...(!skipPayment && {
                meta: {
                    middleware: {
                        payment: true
                    }
                }
            })
        });
    };

export const redirectToAccessoriesCatalogPage = () => {
    return { type: DASH_START_ACCESSORIES_CATALOG };
};

/**
 * Opens the accessories catalog slideout and sets the navigation scope target to the current scenario.
 * This ensures that when accessories are added from the catalog, they go to the correct scenario's scoped store.
 */
export const openAccessoriesCatalogSlideOut = () => (dispatch: any, getState: any) => {
    const state = getState();
    // Get the current scope ID - this will be the scenario from which the user clicked "Add From Catalog"
    const currentScopeId = scopedStateSelectors.getCurrentScopeId(state as any);

    // Set the navigation scope target so the accessories catalog knows which scenario's data to use
    dispatch(scopedActionsCreators.setNavigationScopeTarget(currentScopeId));

    // Open the slideout
    dispatch({ type: OPEN_ACCESSORIES_CATALOG_SLIDEOUT });
};

export const closeAccessoriesCatalogSlideOut = () => ({
    type: CLOSE_ACCESSORIES_CATALOG_SLIDEOUT
});

export const checkAccessoriesCatalogExists = () => (dispatch: any, getState: any) => {
    const state = getState();

    if (!dealerSelectors.isAccessoriesCatalogEnabled(state)) {
        return;
    }

    const dealerId = dealerSelectors.getDealerId(state);
    const { year, make, model, trim, vin } = vehicleSelectors.getVehicle(state);

    const searchParams = {
        dealerId: `${dealerId}`,
        year: `${year}`,
        make: `${make}`,
        model: `${model}`,
        trim: `${trim}`,
        ...(vin && { vin: `${vin}` })
    };

    const queryString = new URLSearchParams(searchParams).toString();

    const locHref = locHrefUtil.getLocHref();
    const urlBuildResult = apiConfigHelper.buildBffApiUrl(MANAGER_VIEW_APP_PREFIX, locHref, 'accessories/catalogExistsCheck');
    const catalogCheckEndpoint = `${urlBuildResult.url}?${queryString}`;

    dispatch({
        [RSAA]: {
            endpoint: catalogCheckEndpoint,
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            types: [
                CHECK_ACCESSORIES_CATALOG_EXISTS_REQUEST,
                CHECK_ACCESSORIES_CATALOG_EXISTS_SUCCESS,
                CHECK_ACCESSORIES_CATALOG_EXISTS_FAILURE
            ]
        }
    });
};
